import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClient } from '@api/client';
import { useNotificationStore } from '@store/notificationStore';
import { useAuthStore } from '@store/authStore';
import { useVendorFieldsStore } from '@store/vendorFieldsStore';
import type { OnboardingConfig } from '@/types/onboarding';
import type { VendorFieldConfig } from '@store/vendorFieldsStore';
import {
  BILL_CREATION_FIELDS,
  FIELDS_BY_DOC_TYPE,
  VENDOR_FIELDS,
} from '@/types/onboarding';

interface OnboardingResponse {
  status: string;
  message: string;
}

export interface OnboardingConfigResponse {
  extraction_fields: {
    purchase_order: string[];
    receipt: string[];
    invoice: string[];
  };
  vendor_fields: string[];
  bill_creation_fields: string[];
}

const buildDefaults = (fields: { key: string; isDefault: boolean }[]) =>
  fields.filter((field) => field.isDefault).map((field) => field.key);

export const buildDefaultOnboardingConfig = (): OnboardingConfigResponse => ({
  extraction_fields: {
    purchase_order: buildDefaults(FIELDS_BY_DOC_TYPE.purchase_order),
    receipt: buildDefaults(FIELDS_BY_DOC_TYPE.receipt),
    invoice: buildDefaults(FIELDS_BY_DOC_TYPE.invoice),
  },
  vendor_fields: buildDefaults(VENDOR_FIELDS),
  bill_creation_fields: buildDefaults(BILL_CREATION_FIELDS),
});

function mapVendorKeysToConfigs(keys: string[]): VendorFieldConfig[] {
  return VENDOR_FIELDS
    .filter((f) => keys.includes(f.key))
    .map((f) => ({
      name: f.key,
      label: f.label,
      required: f.alwaysOn ?? false,
      type: f.key === 'email' ? ('email' as const) : ('text' as const),
    }));
}

export const useSubmitOnboarding = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  const { currentUser, setCurrentUser } = useAuthStore();

  return useMutation({
    mutationFn: async (payload: OnboardingConfig) => {
      const { data } = await apiClient.post<OnboardingResponse>(
        'users/onboarding/complete',
        payload
      );
      return data;
    },
    onSuccess: () => {
      addNotification({
        message: 'Onboarding completed successfully!',
        type: 'success',
      });

      if (currentUser) {
        setCurrentUser({ ...currentUser, onboarding_complete: true });
      }

      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      addNotification({
        message:
          error.response?.data?.detail || 'Failed to complete onboarding',
        type: 'error',
      });
    },
  });
};

export const useOnboardingConfig = (enabled = true) => {
  const { setFields } = useVendorFieldsStore();

  return useQuery({
    queryKey: ['onboarding-config'],
    queryFn: async () => {
      try {
        const { data } = await apiClient.get<OnboardingConfigResponse>(
          'users/onboarding/config'
        );
        setFields(mapVendorKeysToConfigs(data.vendor_fields));
        return data;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          const fallback = buildDefaultOnboardingConfig();
          setFields(mapVendorKeysToConfigs(fallback.vendor_fields));
          return fallback;
        }

        throw error;
      }
    },
    staleTime: Infinity,
    retry: 2,
    enabled,
  });
};
